// @mui material components
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

export default styled(Box)(({ theme, ownerState }) => {
  const { palette, functions, borders, boxShadows } = theme || {};
  const { variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow } = ownerState || {};

  // Make sure we have fallbacks for everything
  const defaultPalette = {
    white: { main: "#ffffff" },
    black: { main: "#000000" },
    transparent: { main: "transparent" },
    grey: { 
      100: "#f8f9fa",
      200: "#e9ecef",
      300: "#dee2e6",
      400: "#ced4da",
      500: "#adb5bd",
      600: "#6c757d",
      700: "#495057",
      800: "#343a40",
      900: "#212529"
    },
    info: { main: "#49a3f1" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    error: { main: "#f5365c" },
    dark: { main: "#344767" },
    light: { main: "#f0f2f5" },
    inputColors: {
      borderColor: { main: "#d2d6da" },
      error: "#f5365c",
      success: "#2dce89"
    },
    text: { main: "#67748e" }
  };

  // Safe function replacements
  const defaultFunctions = {
    linearGradient: (color, colorState) => `linear-gradient(${color}, ${colorState})`,
    boxShadow: () => "none",
    rgba: (color, opacity) => `rgba(${color}, ${opacity})`,
    pxToRem: (px) => `${px / 16}rem`,
  };

  // Default borders
  const defaultBorders = {
    borderColor: { main: "#dee2e6" },
    borderRadius: { 
      xs: "0.125rem",
      sm: "0.25rem",
      md: "0.5rem",
      lg: "0.75rem",
      xl: "1rem",
      xxl: "1.5rem" 
    }
  };

  // Default boxShadows
  const defaultBoxShadows = {
    xs: "0 2px 5px 0 rgba(0,0,0,0.1)",
    sm: "0 4px 6px rgba(0,0,0,0.1)",
    md: "0 8px 15px 0 rgba(0,0,0,0.1)",
    lg: "0 12px 20px 0 rgba(0,0,0,0.1)",
    xl: "0 20px 25px 0 rgba(0,0,0,0.1)",
    xxl: "0 25px 30px 0 rgba(0,0,0,0.1)",
    colored: {
      info: "0 20px 25px 0 rgba(73,163,241,0.5)",
      success: "0 20px 25px 0 rgba(45,206,137,0.5)",
      warning: "0 20px 25px 0 rgba(251,99,64,0.5)",
      error: "0 20px 25px 0 rgba(245,54,92,0.5)",
      dark: "0 20px 25px 0 rgba(52,71,103,0.5)"
    }
  };

  // Use the theme values if available, otherwise use defaults
  const safePalette = palette || defaultPalette;
  const safeFunctions = functions || defaultFunctions;
  const safeBorders = borders || defaultBorders;
  const safeBoxShadows = boxShadows || defaultBoxShadows;

  // Destructure and provide fallbacks
  const { linearGradient, boxShadow, rgba, pxToRem } = safeFunctions;
  const { borderRadius: borderRadiusValue } = safeBorders;
  
  // Get the current color values with fallbacks
  const getColorValue = (colorName) => {
    // Try to access the color from theme.palette
    if (safePalette && safePalette[colorName]) {
      return safePalette[colorName].main;
    }
    
    // Fallback to defaultPalette
    if (defaultPalette[colorName]) {
      return defaultPalette[colorName].main;
    }
    
    // Final fallback - just return a reasonable color
    return colorName === "dark" ? "#344767" : 
           colorName === "light" ? "#f0f2f5" :
           colorName === "white" ? "#ffffff" :
           colorName === "black" ? "#000000" :
           colorName === "info" ? "#49a3f1" :
           colorName === "success" ? "#2dce89" :
           colorName === "warning" ? "#fb6340" :
           colorName === "error" ? "#f5365c" :
           "transparent";
  };

  // Default background color value
  const backgroundValue = bgColor === "transparent"
    ? "transparent"
    : getColorValue(bgColor);

  // Create shadows with fallbacks
  const getShadowValue = () => {
    let shadowValue;
    
    // Get box shadow from theme if available, otherwise use default
    if (shadow) {
      shadowValue = safeBoxShadows[shadow] || defaultBoxShadows[shadow] || "none";
    } else {
      shadowValue = "none";
    }
    
    // Get colored shadow from theme if available, otherwise use default
    let coloredShadowValue = "none";
    if (coloredShadow) {
      coloredShadowValue = 
        (safeBoxShadows && safeBoxShadows.colored && safeBoxShadows.colored[coloredShadow]) || 
        (defaultBoxShadows.colored && defaultBoxShadows.colored[coloredShadow]) || 
        "none";
    }
    
    return { shadowValue, coloredShadowValue };
  };
  
  const { shadowValue, coloredShadowValue } = getShadowValue();

  // Apply styles based on variant
  if (variant === "contained") {
    return {
      opacity: opacity || 1,
      backgroundColor: backgroundValue || "transparent",
      color: color ? getColorValue(color) : getColorValue("dark"),
      borderRadius: borderRadius ? borderRadiusValue[borderRadius] || pxToRem(6) : pxToRem(6),
      boxShadow: shadowValue,
    };
  } else if (variant === "gradient") {
    return {
      opacity: opacity || 1,
      background: backgroundValue 
        ? linearGradient(getColorValue(bgColor), getColorValue(bgColor)) 
        : linearGradient(getColorValue("info"), getColorValue("info")),
      color: color ? getColorValue(color) : getColorValue("white"),
      borderRadius: borderRadius ? borderRadiusValue[borderRadius] || pxToRem(6) : pxToRem(6),
      boxShadow: shadowValue,
    };
  } else {
    return {
      opacity: opacity || 1,
      backgroundColor: backgroundValue || "transparent",
      color: color ? getColorValue(color) : "inherit",
      borderRadius: borderRadius ? borderRadiusValue[borderRadius] || pxToRem(6) : pxToRem(6),
      boxShadow: shadowValue,
    };
  }
});